import { type ChangeEvent } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import styled from 'styled-components'

import * as spriteIcons from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import AddFunds from 'components/UI/Modal/AddFunds/AddFunds'
import { SwitchButton } from 'components-ref/atoms'
import {
    MINIMUM_AMOUNT_OPTIONS,
    RECHARGE_AMOUNT_OPTIONS
} from 'constants/walletConsts'
import { useModalContext } from 'context/modalContext'
import {
    putAutoRecharge,
    putDefaultPaymetMethod
} from 'services/endpoints/payment'
import { useSetUserProfile, useUserProfile } from 'store'
import colors from 'styles/colors'
import { type AutoRecharge } from 'types/api/payment'

export const Funds = () => {
    const modalData = useModalContext()
    const { t } = useTranslation()
    const userData = useUserProfile()!
    const queryClient = useQueryClient()

    const changeDefaultPaymentMethod = useMutation(
        async (data: {
            default_payment_method: 'wallet' | 'card'
        }) => {
            return await putDefaultPaymetMethod(data)
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('user-profile')
            }
        }
    )

    const changeAutoRechargeOption = useMutation(
        async (data: AutoRecharge) => {
            return await putAutoRecharge(data)
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('user-profile')
            }
        }
    )

    const toggleAddFundsModal = () => {
        modalData.addModal(
            t('settings:addFundsModalTitle'),
            <AddFunds defaultCard={userData.default_card} />,
            false, // withoutTitle
            false, // withoutCloseIcon
            '',    // customClass
            '',    // key
            true   // keyboardAdjust
        )
    }

    const mutateAutoRechargeOption = () => {
        const previousAutoRecharge = userData.auto_recharge

        const nextEnabled = !userData.auto_recharge.enabled

        useSetUserProfile({
            auto_recharge: {
                ...userData.auto_recharge,
                enabled: nextEnabled
            }
        })

        changeAutoRechargeOption.mutate(
            {
                is_active: nextEnabled,

                recharge_amount:
                    userData.auto_recharge.recharge_amount > 0
                        ? userData.auto_recharge.recharge_amount
                        : RECHARGE_AMOUNT_OPTIONS[3].value,

                minimum_amount:
                    userData.auto_recharge.minimum_amount > 0
                        ? userData.auto_recharge.minimum_amount
                        : MINIMUM_AMOUNT_OPTIONS[3].value
            },
            {
                onError: () => {
                    useSetUserProfile({
                        auto_recharge: previousAutoRecharge
                    })
                }
            }
        )
    }

    const mutateChangePaymentMethod = () => {
        const previousPaymentMethod =
            userData.default_payment_method

        const nextPaymentMethod =
            previousPaymentMethod === 'wallet'
                ? 'card'
                : 'wallet'

        useSetUserProfile({
            default_payment_method: nextPaymentMethod
        })

        changeDefaultPaymentMethod.mutate(
            {
                default_payment_method: nextPaymentMethod
            },
            {
                onError: () => {
                    useSetUserProfile({
                        default_payment_method:
                            previousPaymentMethod
                    })

                }
            }
        )
    }

    return (
        <FundsContainer className='funds-container'>
            <FundsTitle>{t('Balance')}</FundsTitle>
            <FundsNumberContainer>
                <FundsNumber>
                    <spriteIcons.IconDollar width='40' height='40' />
                    <div>{Number(userData.wallet_deposit).toFixed(2)}</div>
                    <FundsNumberCurrencyText>
                        <div>(USD)</div>
                    </FundsNumberCurrencyText>
                </FundsNumber>
                <Button
                    text={t('addFunds')}
                    color='blue'
                    font='mont-16-semi-bold'
                    width='16'
                    height='5'
                    clickFn={()=>{
                        if(userData?.status === 'restricted' && userData?.role === 'fan' && userData?.default_card !=null){
                            addToast('error', 'Your account is restricted.' )
                            return;
                        }
                        if(userData?.status === 'restricted' && userData?.role === 'fan' && userData?.default_card ==null){
                            addToast('error', 'Please add a payment card.' )
                            return;
                        }
                        return toggleAddFundsModal();
                    }}
                />
            </FundsNumberContainer>
            <FundsOptions>
                <FundsOption>
                    <FundsOptionText>
                        {t('makeWalletPrimaryMethodForRebills')}
                    </FundsOptionText>
                    <SwitchButton
                        active={userData.default_payment_method === 'wallet'}
                        toggle={mutateChangePaymentMethod}
                    />
                </FundsOption>
                <FundsOption>
                    <FundsOptionText>{t('autoRecharge')}</FundsOptionText>
                    <SwitchButton
                        active={userData.auto_recharge.enabled}
                        toggle={mutateAutoRechargeOption}
                    />
                </FundsOption>
                {userData.auto_recharge.enabled && (
                    <FundsOption $padding='0'>
                        <DropdownContainer>
                            <DropdownLabel htmlFor='minimum-amount'>
                                {t('settings:walletMinimumAmount')}
                            </DropdownLabel>

                            <DropdownSelectWrapper>
                                <DropdownSelect
                                    name='minimum-amount'
                                    id='minimum-amount'
                                    value={userData.auto_recharge.minimum_amount}
                                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                        const minimumAmount = parseInt(e.target.value)

                                        const previousAutoRecharge = userData.auto_recharge

                                        const nextAutoRecharge = {
                                            ...userData.auto_recharge,
                                            minimum_amount: minimumAmount
                                        }

                                        useSetUserProfile({
                                            auto_recharge: nextAutoRecharge
                                        })

                                        changeAutoRechargeOption.mutate(
                                            {
                                                is_active: true,
                                                recharge_amount:
                                                    nextAutoRecharge.recharge_amount,
                                                minimum_amount:
                                                    nextAutoRecharge.minimum_amount
                                            },
                                            {
                                                onError: () => {
                                                    useSetUserProfile({
                                                        auto_recharge:
                                                            previousAutoRecharge
                                                    })

                                                }
                                            }
                                        )
                                    }}
                                >
                                    {MINIMUM_AMOUNT_OPTIONS.map(option => (
                                        <option
                                            key={option.text}
                                            value={option.value}
                                        >
                                            {option.text}
                                        </option>
                                    ))}
                                </DropdownSelect>

                                <DropdownArrow />
                            </DropdownSelectWrapper>
                        </DropdownContainer>

                        <DropdownContainer>
                            <DropdownLabel htmlFor='recharge-amount'>
                                {t('settings:walletRechargeAmount')}
                            </DropdownLabel>

                            <DropdownSelectWrapper>
                                <DropdownSelect
                                    name='recharge-amount'
                                    id='recharge-amount'
                                    value={userData.auto_recharge.recharge_amount}
                                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                        const rechargeAmount = parseInt(e.target.value)

                                        const previousAutoRecharge = userData.auto_recharge

                                        const nextAutoRecharge = {
                                            ...userData.auto_recharge,
                                            recharge_amount: rechargeAmount
                                        }

                                        useSetUserProfile({
                                            auto_recharge: nextAutoRecharge
                                        })

                                        changeAutoRechargeOption.mutate(
                                            {
                                                is_active: true,
                                                minimum_amount:
                                                    nextAutoRecharge.minimum_amount,
                                                recharge_amount:
                                                    nextAutoRecharge.recharge_amount
                                            },
                                            {
                                                onError: () => {
                                                    useSetUserProfile({
                                                        auto_recharge:
                                                            previousAutoRecharge
                                                    })

                                                }
                                            }
                                        )
                                    }}
                                >
                                    {RECHARGE_AMOUNT_OPTIONS.map(option => (
                                        <option
                                            key={option.text}
                                            value={option.value}
                                        >
                                            {option.text}
                                        </option>
                                    ))}
                                </DropdownSelect>

                                <DropdownArrow />
                            </DropdownSelectWrapper>
                        </DropdownContainer>
                    </FundsOption>
                )}
            </FundsOptions>
        </FundsContainer>
    )
}

const FundsContainer = styled.div`
    display: flex;
    align-items: center;
    flex-direction: column;
    background-color: ${colors.midnightBlack};
    padding: 2rem;
    padding-top:0.5rem;
    border-radius: 2.5rem 2.5rem 0 0;
`

const FundsTitle = styled.div`
    width: 100%;
    text-align: center;
    font-style: normal;
    font-weight: 600;
    font-size: 1.2rem;
    color: #5f5f5f;
`

const FundsNumberContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-bottom: 2.5rem;
`

const FundsNumber = styled.div`
    display: flex;
    align-items: flex-start;
    font-size: 5rem;
    line-height: 5rem;
    color: ${colors.white};
    margin: 1.5rem 0;
    margin-top:1rem;
    margin-bottom:2rem;
`

const FundsNumberCurrencyText = styled.span`
    font-size: 1.2rem;
    line-height: 3rem;
    color: ${colors.skyBlue};
`

const FundsOptions = styled.div`
    display: flex;
    flex-direction: column;
`

const FundsOption = styled.div<{ $padding?: string }>`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${props => (props.$padding ? props.$padding : '1.6rem 0')};
`

const FundsOptionText = styled.div`
    max-width: 70%;
    font-weight: 600;
    font-size: 1.4rem;
    color: ${colors.charcoalGray};
`

const DropdownContainer = styled.div`
    display: flex;
    flex-direction: column;
`

const DropdownLabel = styled.label`
    font-weight: 600;
    font-size: 1.2rem;
    color: ${colors.stormyBlue};
    margin-bottom: 0.5rem;
`

const DropdownSelectWrapper = styled.div`
    position: relative;
`

const DropdownSelect = styled.select`
    height: 3.6rem;
    display: flex;
    align-items: center;
    font-family: 'Montserrat', sans-serif;
    font-weight: 600;
    font-size: 1.2rem;
    line-height: 1.2rem;
    color: ${colors.white};
    background-color: #262a33;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 2rem;

    padding: 0 3.5rem 0 1.5rem;

    appearance: none;
    -webkit-appearance: none;
    &:focus,
    &:focus-visible {
        outline: none;
        box-shadow: none;
        border-color: ${colors.skyBlue};
    }
`

const DropdownArrow = styled.span`
    position: absolute;
    top: 50%;
    right: 1.4rem;

    width: 0.7rem;
    height: 0.7rem;

    border-right: 2px solid ${colors.white};
    border-bottom: 2px solid ${colors.white};

    transform: translateY(-65%) rotate(45deg);

    pointer-events: none;
`